import type { Vetor3 } from '../geometria/vetor3'

export type TipoObstaculoExterno = 'outroVeiculo' | 'poste' | 'muro'

export interface ObstaculoExterno {
  id: string
  tipo: TipoObstaculoExterno
  x: number
  z: number
}

export const NOMES_OBSTACULO_EXTERNO: Record<TipoObstaculoExterno, string> = {
  outroVeiculo: 'Outro veículo',
  poste: 'Poste',
  muro: 'Muro',
}

export const RAIO_INTERFERENCIA: Record<TipoObstaculoExterno, number> = {
  outroVeiculo: 1.05,
  poste: 0.42,
  muro: 0.72,
}

function distanciaAoSegmento(p: { x: number; z: number }, a: Vetor3, b: Vetor3): number {
  const dx = b.x - a.x
  const dz = b.z - a.z
  const quadrado = dx * dx + dz * dz
  const t = quadrado < 1e-9 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.z - a.z) * dz) / quadrado))
  return Math.hypot(p.x - (a.x + dx * t), p.z - (a.z + dz * t))
}

export function distanciaDaPolilinha(p: { x: number; z: number }, pontos: readonly Vetor3[]): number {
  if (pontos.length === 0) return Number.POSITIVE_INFINITY
  if (pontos.length === 1) return Math.hypot(p.x - pontos[0]!.x, p.z - pontos[0]!.z)
  return Math.min(...pontos.slice(1).map((fim, i) => distanciaAoSegmento(p, pontos[i]!, fim)))
}

export function obstaculosQueInterferem(
  obstaculos: readonly ObstaculoExterno[],
  pontos: readonly Vetor3[],
): ObstaculoExterno[] {
  return obstaculos.filter((o) => distanciaDaPolilinha(o, pontos) <= RAIO_INTERFERENCIA[o.tipo])
}

export type IdAberturaVeiculo =
  | 'portaDianteiraEsquerda'
  | 'portaDianteiraDireita'
  | 'portaTraseiraEsquerda'
  | 'portaTraseiraDireita'
  | 'tampaTraseira'

export interface FaixaPortaPerfil {
  zDianteiro: number
  zTraseiro: number
}

export interface PerfilParaAbertura {
  largura: number
  comprimento: number
  traseiraZ: number
  portaDianteira: FaixaPortaPerfil
  portaTraseira: FaixaPortaPerfil
}

/** Giro máximo da porta em torno da dobradiça, em radianos. */
export const ANGULO_ABERTURA_PORTA = 0.95

/** Folga entre a porta e o obstáculo para considerar contato. */
const FOLGA_CONTATO_PORTA = 0.04

/** Meias-dimensões (x, z) da pegada do obstáculo no solo, iguais às da cena. */
function pegadaDoObstaculo(o: ObstaculoExterno, perfil: PerfilParaAbertura): { meioX: number; meioZ: number } {
  if (o.tipo === 'poste') return { meioX: 0.18, meioZ: 0.18 }
  if (o.tipo === 'muro') return { meioX: 0.14, meioZ: 1.6 }
  return { meioX: perfil.largura / 2, meioZ: perfil.comprimento * 0.41 }
}

/**
 * A porta gira em torno da dobradiça, na borda dianteira, e varre um setor
 * para fora do veículo. Só bloqueia a porta o obstáculo que esse setor alcança:
 * um poste junto à porta do motorista não impede a porta traseira do mesmo lado.
 */
function setorDaPortaToca(
  idAbertura: Exclude<IdAberturaVeiculo, 'tampaTraseira'>,
  perfil: PerfilParaAbertura,
  o: ObstaculoExterno,
): boolean {
  const sinal = idAbertura.endsWith('Esquerda') ? 1 : -1
  const faixa = idAbertura.startsWith('portaDianteira') ? perfil.portaDianteira : perfil.portaTraseira
  const comprimento = faixa.zDianteiro - faixa.zTraseiro
  const { meioX, meioZ } = pegadaDoObstaculo(o, perfil)
  const xDobradica = sinal * perfil.largura / 2

  const PASSOS_GIRO = 16
  const PASSOS_PORTA = 16
  for (let g = 0; g <= PASSOS_GIRO; g++) {
    const angulo = (ANGULO_ABERTURA_PORTA * g) / PASSOS_GIRO
    for (let p = 1; p <= PASSOS_PORTA; p++) {
      const d = (comprimento * p) / PASSOS_PORTA
      const x = xDobradica + sinal * d * Math.sin(angulo)
      const z = faixa.zDianteiro - d * Math.cos(angulo)
      const dx = Math.max(0, Math.abs(x - o.x) - meioX)
      const dz = Math.max(0, Math.abs(z - o.z) - meioZ)
      if (Math.hypot(dx, dz) <= FOLGA_CONTATO_PORTA) return true
    }
  }
  return false
}

/**
 * Avalia se um obstáculo externo bloqueia fisicamente o raio de abertura de uma porta ou tampa.
 */
export function portaObstruidaPorObstaculo(
  idAbertura: IdAberturaVeiculo,
  perfil: PerfilParaAbertura,
  obstaculos: readonly ObstaculoExterno[],
): boolean {
  if (obstaculos.length === 0) return false

  if (idAbertura === 'tampaTraseira') {
    return obstaculos.some((o) => {
      const raio = RAIO_INTERFERENCIA[o.tipo]
      const dz = Math.max(0, perfil.traseiraZ - o.z)
      const dx = Math.max(0, Math.abs(o.x) - perfil.largura / 2)
      return o.z <= perfil.traseiraZ + 0.3 && Math.hypot(dx, dz) <= raio + 0.4
    })
  }

  return obstaculos.some((o) => setorDaPortaToca(idAbertura, perfil, o))
}
